import { useCallback, useEffect, useMemo, useRef } from 'react'

import { Skeleton } from '@mantine/core'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_PIPELINE_STAGES_BY_JOB_LISTING } from 'src/graphql/pipelineMetrics.graphql'

import AnalyticsCard from '../../components/AnalyticsCard'

import { buildApplicantsFilterUrl } from '../buildApplicantsFilterUrl'
import { STAGE_ORDER } from './stagePalette'

import type {
  ApplicantStage,
  JobStatus,
  PipelineApplicantFiltersInput,
  PipelineStagesByJobListingRow as RowType,
} from 'types/graphql'

const INFO_TEXT =
  'Applicant counts per job listing, broken out by stage. Each stage counts on its own date column (PROSPECT on applicationDate, HIRED on hiredDate, etc.) — so column totals reconcile with the Pipeline Summary card above.'

const TITLE = 'Pipeline by Job Listing'

// Subset of the GraphQL row shape that this View needs. Keeping the prop
// type narrow makes it trivial to render in Storybook + unit tests without
// fabricating the full GraphQL response shape.
export type StagesByListingRow = Pick<RowType, 'jobListingId' | 'total'> & {
  jobListing: { jobListingId: string; title: string; status: JobStatus }
  stages: ReadonlyArray<{ stage: ApplicantStage; count: number }>
}

interface PipelineStagesByJobListingViewProps {
  rows: ReadonlyArray<StagesByListingRow> | undefined
  loading: boolean
  /**
   * When provided, every per-row numeric cell becomes clickable. The View
   * itself stays router-agnostic — the wrapper hooks this up to a deep-link
   * into the Applicants page. Omit for static rendering (e.g. Storybook,
   * snapshot tests).
   */
  onCellClick?: (params: {
    jobListingId: string
    stage: ApplicantStage
  }) => void
  /**
   * When provided, the footer "Total" cells for each stage become clickable
   * too — wired to the same deep-link target but without a jobListingId
   * filter (sums across all listings in view).
   */
  onTotalClick?: (params: { stage: ApplicantStage }) => void
}

/**
 * Presentational component — pure function of `rows` + `loading`. The Apollo
 * wrapper below is used in production; this split keeps Storybook + tests
 * free of MockedProvider boilerplate for the basic rendering cases.
 */
export const PipelineStagesByJobListingView = ({
  rows,
  loading,
  onCellClick,
  onTotalClick,
}: PipelineStagesByJobListingViewProps) => {
  // Column totals — one number per stage, summed across every listing in
  // the table. Rendered as a footer row so the user can scan e.g. "total
  // hired across all listings" without needing a per-row Total column.
  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    for (const s of STAGE_ORDER) totals[s.stage] = 0
    for (const r of rows ?? []) {
      for (const cell of r.stages) {
        totals[cell.stage] = (totals[cell.stage] ?? 0) + cell.count
      }
    }
    return totals
  }, [rows])

  // ---- Loading ----
  if (loading && !rows) {
    return (
      <AnalyticsCard title={TITLE} info={INFO_TEXT}>
        <div className="overflow-x-auto" data-testid="stages-by-listing-loading">
          <table className="w-full border-separate border-spacing-1 font-mono text-[11px]">
            <thead>
              <tr>
                <th className="w-[180px]" />
                {STAGE_ORDER.map((s) => (
                  <th key={s.stage} className="px-2 py-1.5">
                    <Skeleton height={10} width={64} radius="sm" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, ri) => (
                <tr key={ri}>
                  <th className="pr-2 text-right">
                    <Skeleton height={12} width={120} radius="sm" />
                  </th>
                  {STAGE_ORDER.map((s) => (
                    <td key={s.stage} className="px-2 py-2">
                      <Skeleton height={28} radius="sm" />
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <th className="pr-2 text-right">
                  <Skeleton height={12} width={50} radius="sm" />
                </th>
                {STAGE_ORDER.map((s) => (
                  <td key={s.stage} className="px-2 py-2">
                    <Skeleton height={20} radius="sm" />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </AnalyticsCard>
    )
  }

  // ---- Empty ----
  if (!rows || rows.length === 0) {
    return (
      <AnalyticsCard title={TITLE} info={INFO_TEXT}>
        <p
          className="py-2 text-sm text-doubleNickel-gray-500"
          data-testid="stages-by-listing-empty"
        >
          No job listings with activity in this period
        </p>
      </AnalyticsCard>
    )
  }

  // ---- Loaded ----
  return (
    <AnalyticsCard title={TITLE} info={INFO_TEXT}>
      <div
        className="overflow-x-auto"
        data-testid="stages-by-listing-table-wrap"
      >
        <table
          className="w-full border-separate border-spacing-1 font-mono text-[11px]"
          data-testid="stages-by-listing-table"
        >
          <thead>
            <tr>
              <th className="w-[180px] pr-2 text-right text-[10.5px] font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
                Job Listing
              </th>
              {STAGE_ORDER.map((s) => (
                <th
                  key={s.stage}
                  className="px-2 py-1.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-doubleNickel-gray-700"
                  data-testid="stages-by-listing-col-header"
                >
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.jobListingId} data-testid="stages-by-listing-row">
                <th
                  className="pr-2 text-right text-[11px] font-medium text-doubleNickel-gray-700"
                  title={row.jobListing.title}
                >
                  {row.jobListing.title}
                </th>
                {STAGE_ORDER.map((s) => {
                  const cell = row.stages.find((x) => x.stage === s.stage)
                  const count = cell?.count ?? 0
                  const handleClick = onCellClick
                    ? () =>
                        onCellClick({
                          jobListingId: row.jobListingId,
                          stage: s.stage,
                        })
                    : undefined
                  const clickable = typeof handleClick === 'function'
                  return (
                    <td
                      key={s.stage}
                      className={[
                        'text-center text-[11.5px]',
                        count === 0
                          ? 'text-doubleNickel-gray-400'
                          : 'text-doubleNickel-gray-900',
                        clickable
                          ? 'cursor-pointer transition-colors hover:bg-doubleNickel-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-doubleNickel-brand-300'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ padding: '9px 6px', minWidth: 64 }}
                      data-testid="stages-by-listing-cell"
                      data-stage={s.stage}
                      data-count={count}
                      onClick={handleClick}
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleClick()
                              }
                            }
                          : undefined
                      }
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      aria-label={
                        clickable
                          ? `${count.toLocaleString()} ${s.label} for ${row.jobListing.title}`
                          : undefined
                      }
                    >
                      {count === 0 ? '—' : count.toLocaleString()}
                    </td>
                  )
                })}
              </tr>
            ))}
            {/* Footer row: per-stage totals across all listings in view.
                A thin top border separates the totals from the data rows. */}
            <tr data-testid="stages-by-listing-totals-row">
              <th className="border-t border-doubleNickel-gray-200 pr-2 pt-2 text-right text-[11px] font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
                Total
              </th>
              {STAGE_ORDER.map((s) => {
                const total = columnTotals[s.stage] ?? 0
                const handleClick = onTotalClick
                  ? () => onTotalClick({ stage: s.stage })
                  : undefined
                const clickable = typeof handleClick === 'function'
                return (
                  <td
                    key={s.stage}
                    className={[
                      'border-t border-doubleNickel-gray-200 pt-2 text-center text-[11.5px] font-semibold text-doubleNickel-gray-900',
                      clickable
                        ? 'cursor-pointer transition-colors hover:bg-doubleNickel-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-doubleNickel-brand-300'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{ padding: '9px 6px', minWidth: 64 }}
                    data-testid="stages-by-listing-total"
                    data-stage={s.stage}
                    data-count={total}
                    onClick={handleClick}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleClick()
                            }
                          }
                        : undefined
                    }
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    aria-label={
                      clickable
                        ? `${total.toLocaleString()} ${s.label} across all listings`
                        : undefined
                    }
                  >
                    {total.toLocaleString()}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </AnalyticsCard>
  )
}

interface PipelineStagesByJobListingCardProps {
  filters: PipelineApplicantFiltersInput
  /**
   * Bumped by the page-level Refresh button. The card ignores the initial
   * mount (useQuery already fetched) and refetches on subsequent changes.
   */
  refreshKey: number
}

const PipelineStagesByJobListingCard = ({
  filters,
  refreshKey,
}: PipelineStagesByJobListingCardProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(
    GET_PIPELINE_STAGES_BY_JOB_LISTING,
    {
      variables: { input: { filters } },
      onError,
      notifyOnNetworkStatusChange: true,
    }
  )

  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  // The View accepts an `onCellClick` so it stays router-agnostic for
  // Storybook/tests. Here in the production wrapper we deep-link into the
  // Applicants page using the same date range that drove the query — so the
  // resulting list matches the count the user clicked. We only enable the
  // click affordance when both endpoints of the date range are present —
  // a half-open range can't drive the Applicants page's date-range filters.
  const { gte, lte } = filters.dateRange ?? {}
  const clickableDateRange =
    gte && lte ? { gte: new Date(gte), lte: new Date(lte) } : null
  const handleCellClick = useCallback(
    ({
      jobListingId,
      stage,
    }: {
      jobListingId: string
      stage: ApplicantStage
    }) => {
      if (!clickableDateRange) return
      navigate(
        buildApplicantsFilterUrl({
          stage,
          dateRange: clickableDateRange,
          jobListingId,
        })
      )
    },
    [clickableDateRange]
  )

  // Footer totals deep-link to the same /applicants filter as the per-row
  // cells but without a jobListingId filter — they represent the column
  // sum across all listings in view. The resolver here ignores recruiter /
  // jobListing / leadSource facets (see comment near the call site in
  // AnalyticsPageV2.tsx), so the deep-link mirrors that and applies date
  // + stage only.
  const handleTotalClick = useCallback(
    ({ stage }: { stage: ApplicantStage }) => {
      if (!clickableDateRange) return
      navigate(
        buildApplicantsFilterUrl({
          stage,
          dateRange: clickableDateRange,
        })
      )
    },
    [clickableDateRange]
  )

  return (
    <PipelineStagesByJobListingView
      rows={data?.pipelineStagesByJobListing}
      loading={loading}
      onCellClick={clickableDateRange ? handleCellClick : undefined}
      onTotalClick={clickableDateRange ? handleTotalClick : undefined}
    />
  )
}

export default PipelineStagesByJobListingCard
